import React from 'react';
import { ChainState } from '../../types/api';

interface ChainStatusProps {
  chains?: ChainState[];
}

const ChainStatus: React.FC<ChainStatusProps> = ({ chains }) => {
  if (!chains || chains.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Chain Status</h2>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Loading chain status...</p>
        </div>
      </div>
    );
  }

  const getChainName = (chainId: number) => {
    switch (chainId) {
      case 56:
        return 'BSC Mainnet';
      case 97:
        return 'BSC Testnet';
      case 2020:
        return 'Ronin Mainnet';
      case 2021:
        return 'Ronin Testnet';
      default:
        return `Chain ${chainId}`;
    }
  };

  const formatBalance = (balance: string) => {
    const num = parseFloat(balance);
    if (num === 0) return '0';
    if (num < 1000) return num.toFixed(2);
    if (num < 1000000) return `${(num / 1000).toFixed(1)}K`;
    return `${(num / 1000000).toFixed(1)}M`;
  };

  const getTimeSince = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return `${seconds}s ago`;
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Chain Status</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {chains.sort((a, b) => b.chainId - a.chainId).map((chain) => (
          <div key={chain.chainId} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-medium text-gray-900">
                {getChainName(chain.chainId)}
              </h3>
              <div className={`flex items-center space-x-2 ${
                chain.isHealthy ? 'text-green-600' : 'text-red-600'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  chain.isHealthy ? 'bg-green-500' : 'bg-red-500'
                }`}></div>
                <span className="text-sm font-medium">
                  {chain.isHealthy ? 'Healthy' : 'Unhealthy'}
                </span>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Latest Block:</span>
                <span className="font-mono">{chain.latestBlock.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Processed Block:</span>
                <span className="font-mono">{chain.lastProcessedBlock.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Block Lag:</span>
                <span className={`font-mono ${
                  chain.latestBlock - chain.lastProcessedBlock > 10 
                    ? 'text-orange-600' 
                    : 'text-green-600'
                }`}>
                  {chain.latestBlock - chain.lastProcessedBlock}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Balance:</span>
                <span className="font-mono">{formatBalance((BigInt(chain.balance) / BigInt(10 ** 18)).toString())} PFVS</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Last Check:</span>
                <span>{getTimeSince(chain.lastCheckTime)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">RPC Status:</span>
                <span className="text-green-600">
                  {chain.activeRpcIndex + 1}/{chain.rpcEndpoints.length}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChainStatus; 